<template>
  <div class="card p-2" style="min-height: calc(100vh - 185px);">
    <div class="d-flex flex-wrap justify-content-between align-item-center">
      <h3>Service Resource (List View)</h3>
      <div class="gx-1">
        <button
            style="min-width: 64px;"
            @click="onClickTreeView"
            class="btn btn-outline-secondary waves-effect waves-float waves-light me-1"
        > <i class="fa fa-tree" aria-hidden="true"></i> Tree View
        </button>
      </div>
    </div>

    <div class="mt-2"></div>
    <div class="row mb-2">
      <div class="col-12 col-md-3">
        <label for="resource-type">Select Resource Type</label>
        <v-select
            placeholder="Select Resource Type"
            v-model="resourceType"
            :options="resourceTypes"
            label="name"
            :reduce="types => types.value"
        />
      </div>

      <div class="col-12 col-md-3">
        <label for="resource-group">Select Resource Group</label>
        <v-select
            placeholder="Select Resource Group"
            v-model="serviceResourceGroup"
            :options="serviceResourceGroups"
            label="name"
            :reduce="types => types.id"
        />
      </div>

      <div class="col-12 col-md-3">
        <label for="service-resource">Select Service Resource</label>
        <AsyncSelect
            placeholder="Select Service Resource"
            v-model="serviceResourceName"
            :api-service="fetchServiceList"
            :additional-query="{ is_all: true }"
            :format-label="formatPatientLabel"
        />
      </div>

      <div  class="col-md-2 d-flex align-items-end mt-1 mt-md-0 action-btn-setter">
        <button
            style="min-width: 64px;"
            @click="onClickGo"
            class="btn btn-primary btn-block waves-effect waves-float waves-light"
        > Go
        </button>
      </div>
    </div>

    <table class="table table-bordered">
      <thead>
      <tr>
        <th>Resource Type</th>
        <th>Service Resource Group</th>
        <th>Service Resource Name</th>
        <th>Status</th>
        <th>Action</th>
      </tr>
      </thead>
      <tbody>
         <tr v-for="(item, i) in serviceResourceList" :key="i">
           <td>{{ item.service_resource_type }}</td>
           <td>{{ item.service_resource_name }}</td>
           <td>{{ item.name }}</td>
           <td>{{ item.service_general_status }}</td>
           <td>
             <img @click="emitResourceEdit(item)" width="13" class="cursor-pointer"
                  src="/app-assets/images/icons/pencil-alt-solid.svg" alt="">
           </td>
        </tr>
      </tbody>
    </table>

    <ServiceResourceAddModal
        :showParent="showParent"
        :modalTitle="resourceTitle"
        ref="resourceModalRef"
        :type="homeContent.resource_type"
        :formData="resourceFormData"
        :specifications="homeContent.resource_specifications"
        :contacts="contactProfiles"
        :contactProfile="contactProfile"
        :products="products"
        :userList="userList"
        :accountHeads="accountHeads"
        :group="serviceResourceGroups"
        @onSubmit="handleResourceSubmit"
    />

    <Loader v-if="loading"/>
    <div class="px-2"><Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/></div>
  </div>
</template>

<script setup>
import {computed, inject, onMounted, ref} from 'vue'
import { useStore } from 'vuex'
import {useRoute, useRouter} from "vue-router";
import handleHospital from '@/services/modules/hospital'
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import handleInventory from "@/services/modules/inventory";
import ServiceResourceAddModal from '@/components/molecule/company/hospital/service-resources/ServiceResourceAddModal'
import handleCompanyContact from '@/services/modules/companyContact'
import handlePurchaseAndSales from "@/services/modules/purchase";
import handleImportTracker from "@/services/modules/procurement/importTracker";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";

const router = useRouter()
const route = useRoute()
let offset = ref(20)
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const resourceTitle = ref('Add Resource')
const loading = ref(false)
const store = useStore()
const serviceResourceList = ref([]);
const resourceType = ref('');
const resourceTypes = ref([]);
const serviceResourceName = ref('');
const serviceResourceGroup = ref('');
const serviceResourceGroups = ref([]);
const page = computed(() => {
  return route.query.page
})
const companyId = computed(() => route.params.companyId);
const products = ref([])
const userList = ref([]);
const accountHeads = ref([])
const emitter = inject('emitter');
const showParent = ref(false)
const resourceModalRef = ref(null)
const homeContent = ref({
  resource_type: {},
  resource_specifications: {}
})
const contactProfiles = ref([])
const contactProfile = ref(null)

const { formatPatientLabel } = useAsyncDropdownHelper()
const {
  fetchServiceResourceListView,
  updateServiceResource,
  fetchGroup,
  fetchServiceList
} = handleHospital()
const { fetchHome, fetchAccountHeads} = handleInventory();
const {fetchCompanyAllContacts} = handleCompanyContact();
const {fetchProductList} = handlePurchaseAndSales();
const { fetchContacts } = handleImportTracker();

const resourceFormData = ref({
  contact_profile_id: '',
  service_user_id: null,
  name: '',
  service_code: '',
  short_description: '',
  full_description: '',
  resource_type: null,
  top_margin: 170,
  bottom_margin: 86,
  left_margin: 25,
  right_margin: 25,
  status: 'active',
  company_id: '',
  head_group_id: null,
  resource_descriptions: [
    {
      product_id: '',
      service_resource_id: '',
      sales_account_head_id: '',
      sales_price: 0,
      cost_of_sales_account_head_id: '',
      cost_of_sales_price: 0,
      service_specification: '',
      service_duration: '',
      is_opd_billing: false,
      is_repeat_weekly: 1,
      schedules: '',
    }
  ]
})

const onClickGo = () => {
  getServiceResourceList()
}

const emitResourceEdit = (item) => {
  getGroups(item.resource_type)
  emitter.emit('onUpdateResource', {
    eventName: 'onUpdateLocation',
    title: 'Edit Resource',
    data: item
  })
}

emitter.on('onUpdateResource', async ({title, data}) => {
  const {
    id,
    name,
    service_code,
    resource_type,
    contact_profile_id,
    service_resource_id,
    service_user_id,
    short_description,
    full_description,
    top_margin,
    bottom_margin,
    left_margin,
    right_margin,
    consultant,
    resource_descriptions
  } = data;
  let formattedData = {
    id,
    name,
    service_code,
    resource_type,
    contact_profile_id,
    service_user_id,
    short_description,
    full_description,
    top_margin,
    bottom_margin,
    left_margin,
    right_margin,
    resource_descriptions,
    status: 'active',
    company_id: companyId.value,
    head_group_id: service_resource_id,
    _method: 'PUT'
  }
  contactProfile.value = {
    id: contact_profile_id,
    name: consultant && consultant.full_name
  }
  resourceTitle.value = title
  showParent.value = true
  resourceFormData.value = formattedData
  resourceModalRef.value.toggleModal()
})

const handleResourceSubmit = async () => {
  try {
    loading.value = true
    resourceFormData.value.company_id = companyId.value
    let res =await updateServiceResource(resourceFormData.value)
    if (!res.status) {
      showError(res.message)
    }
    if (res.status) {
      resourceModalRef.value.toggleModal()
      await getServiceResourceList()
      showSuccess(res.message)
      showParent.value = false
    }
  } catch (err) {
    if (!err.response) {
      showError('Something is wrong. Check your connectivity!!')
    }
    if (err.response) {
      showError(err.response.message)
    }
  } finally {
    loading.value = false
  }
}

const getGroups = async (accountType = '') => {
  try {
    loading.value = true
    let res = await fetchGroup(companyId.value, accountType)
    if (!res.status) {
      serviceResourceGroups.value = []
    }
    if (res.status) {
      serviceResourceGroups.value = res.data
    }
  } catch (err) {
    if (!err.response) {
      showError('Something is wrong. Check your connectivity!!')
    }
    if (err.response) {
      showError(err.response.message)
    }
  } finally {
    loading.value = false
  }
}
const getQuery = () => {
  let query = `?company_id=${companyId.value}`
  query += '&offset='+offset.value
  if(page.value) query += '&page='+page.value
  if(resourceType.value) query += '&resource_type='+resourceType.value
  if(serviceResourceGroup.value) query += '&service_resource_group='+serviceResourceGroup.value
  if(serviceResourceName.value) query += '&service_resource_id='+serviceResourceName.value.id
  return query;
}

const getServiceResourceList = async () => {
  let query = getQuery();
  try {
    loading.value = true
    let res = await fetchServiceResourceListView(query)
    if (!res.status) {
      store.commit("resetPagination")
      return serviceResourceList.value = []
    }
    serviceResourceList.value = res.data.data
    setPagination(res.data)
  } catch (err) {
    if (!err.response) {
      showError('Something is wrong. Check your connectivity!!')
    }
    if (err.response) {
      showError(err.response.message)
    }
  } finally {
    loading.value = false
  }
}

const onPageChange  = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await getServiceResourceList()
}

function setPagination(data) {
  if(!data.data.length) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onClickTreeView  = () => {
  router.push({name: 'service-resources',
    params: {
      companyId:route.params.companyId,
      moduleId: route.params.moduleId,
      menuId: route.params.menuId,
      pageId: route.params.pageId,
      accountType:'human_resources',
    }
  });
}

const getSearchQuery = () => {
  let query = '?company_id=' + companyId.value
  query += '&dropdown=' + true
  return query
}

onMounted(async () => {
  const homeRes = fetchHome();
  let proQuery = getQuery()
  proQuery += '&product_type=services'
  const productsRes = fetchProductList(proQuery);
  loading.value = true
  const contactProfileRes = fetchCompanyAllContacts(getSearchQuery());
  const accountHeadRes = fetchAccountHeads(companyId.value);

  await Promise.all([
    homeRes.then(({data}) => {
      if (data) {
        const {resource_type, resource_specifications} = data;
        resourceTypes.value = Object.keys(resource_type).map(key => ({
          value: key,
          name: resource_type[key]
        }));
        homeContent.value.resource_type = resource_type;
        homeContent.value.resource_specifications = resource_specifications;
      }
    }),
    contactProfileRes.then(({data}) => {
      if (data) contactProfiles.value = data
    }),
    productsRes.then(({data}) => {
      if (data) products.value = data
    }),
    fetchContacts(proQuery).then(({data}) => {
      if(data) userList.value = data;
    }),
    accountHeadRes.then(({data}) => {
      if (data) accountHeads.value = data
    }),
  ]).then(() => {
    loading.value = false;
  }).catch((err) => {
    loading.value = false
  });

  await getGroups()
})


</script>
